'use client';

import { Timer, ShieldAlert } from 'lucide-react';

const OOT_STEPS = [
  'Approved as-found failure or manual flag',
  'Assessment window confirmed',
  'Blast Radius evidence reviewed',
  'Investigation findings documented',
  'Final disposition and sign-off captured',
  'Evidence PDF archived',
] as const;

export function DifferentiatorsSection() {
  return (
    <section className="py-16 bg-background border-y border-border/50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary mb-3">
            What&apos;s Different
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Two things we do that other calibration tools don&apos;t.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <article className="rounded-2xl border border-border bg-card p-7">
            <div className="inline-flex items-center gap-2 rounded-md bg-green-500/10 text-green-700 dark:text-green-300 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide mb-4">
              <Timer className="w-3.5 h-3.5" />
              &lt; 60 seconds
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">60-second evidence retrieval</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Generate status, full history, certificates, OOT records, and audit trail for any instrument on demand.
              No folder-diving and no stitching evidence from multiple systems.
            </p>
          </article>

          <article className="rounded-2xl border border-border bg-card p-7">
            <div className="inline-flex items-center gap-2 rounded-md bg-primary/10 text-primary px-2.5 py-1 text-xs font-semibold uppercase tracking-wide mb-4">
              <ShieldAlert className="w-3.5 h-3.5" />
              Mandatory workflow
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">Controlled OOT workflow with Blast Radius review</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              When an approved as-found failure is released, or Quality flags a manual case, Scopax opens an
              OOT investigation and guides the team through the assessment window, Blast Radius evidence,
              investigation findings, and sign-off. Blast Radius shows potentially affected uses, coverage,
              and evidence limitations from usage logs plus manual evidence.
            </p>

            <ol className="space-y-2">
              {OOT_STEPS.map((step, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-muted-foreground">{step}</span>
                </li>
              ))}
            </ol>

            <p className="mt-4 text-xs font-medium text-primary/80">Closure checks surface missing evidence before sign-off.</p>
          </article>
        </div>
      </div>
    </section>
  );
}
